import { useEffect, useRef } from 'react';

// TS's lib.dom.d.ts doesn't include the Screen Wake Lock API yet — declare the
// minimal shape used here rather than pulling in a global augmentation.
interface WakeLockSentinel {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
}

interface WakeLockNavigator extends Navigator {
  wakeLock: {
    request: (type: 'screen') => Promise<WakeLockSentinel>;
  };
}

// Keeps the screen (and, on most laptops, the whole system by extension) from going
// to sleep while `isActive` is true. Needed for flows like voice AI role-play where the
// learner only speaks — there's no keyboard/mouse activity to reset the OS idle timer,
// so without this the machine can go to sleep mid-conversation.
const useWakeLock = (isActive: boolean) => {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!isActive || !('wakeLock' in navigator)) {
      return undefined;
    }

    let cancelled = false;

    const requestLock = async () => {
      try {
        const sentinel = await (navigator as WakeLockNavigator).wakeLock.request('screen');
        if (cancelled) {
          sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        // The browser releases the lock on its own whenever the document goes hidden
        // (not just when we call release() ourselves) — clear the ref then too, so
        // onVisibilityChange's `!sentinelRef.current` check knows to re-request.
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
          }
        });
      } catch {
        // Request can reject (e.g. document hidden, low battery) — nothing to fall back to.
      }
    };

    requestLock();

    // The OS/browser releases the lock automatically when the tab is backgrounded —
    // reacquire it when the learner comes back while the conversation is still active.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !sentinelRef.current) {
        requestLock();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      sentinelRef.current?.release().catch(() => {});
      sentinelRef.current = null;
    };
  }, [isActive]);
};

export default useWakeLock;
